// ============================================================
// DaySummaryCard.tsx
// AI day summary — a clean surface card (NOT a forest block) with a subtle
// "AI" marker. Used on the Dashboard (today) and in the calendar's day panel
// (the opened day).
//
// Talks only to OUR backend (fetchDaySummaryApi / refreshDaySummaryApi);
// the AI call + key live server-side. States:
//   loading  → "Generating your summary…" skeleton
//   ai/cache → the summary + "Generated HH:MM" + refresh
//   fallback → the plain list the backend returns when AI is unavailable
//   empty    → a light "nothing scheduled" line
//   error    → request failed entirely → short message + Try again
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import { fetchDaySummaryApi, refreshDaySummaryApi, DaySummary } from '../../api/casevoApi'

interface DaySummaryCardProps {
  /** 'YYYY-MM-DD' — the day to summarise. */
  date: string
  /** 'dashboard' = roomy card with "AI · Your day"; 'panel' = compact, for the day panel;
   *  'briefing' = the dashboard's two-part "Daily briefing" card (mockup option 2). */
  variant?: 'dashboard' | 'panel' | 'briefing'
  /** Optional fingerprint of the day's items (e.g. the calendar day panel's
   *  entries). When it CHANGES (an item added / moved / deleted), the card
   *  re-loads — the server dropped its cached summary on that change, so the
   *  load regenerates from live data instead of showing deleted items. */
  dataVersion?: string
  /** Extra classes on the outer card (e.g. the raised shadow when floated over the hero). */
  className?: string
}

// Pull a readable message out of an axios error (e.g. the 429 throttle).
const errorMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { error?: string } }; message?: string }
  return e?.response?.data?.error || fallback
}

const SparkleIcon: React.FC<{ className?: string; size?: number }> = ({ className, size = 14 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </svg>
)

const RefreshIcon: React.FC<{ spinning: boolean }> = ({ spinning }) => (
  <svg className={spinning ? 'animate-spin motion-reduce:animate-none' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)

// Render the model's plain text: "- " lines become a list, anything else a paragraph.
const SummaryBody: React.FC<{ text: string; compact: boolean; large?: boolean }> = ({ text, compact, large }) => {
  const blocks: React.ReactNode[] = []
  let bullets: string[] = []
  const flush = (): void => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2.5">
              <span aria-hidden="true" className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-casevo-mid" />
              <span>{b}</span>
            </li>
          ))}
        </ul>,
      )
      bullets = []
    }
  }
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line) { flush(); continue }
    const m = line.match(/^[-•*]\s+(.*)$/)
    if (m) { bullets.push(m[1]); continue }
    flush()
    // Briefing: the opening sentence reads at the mockup's 17px lead size.
    const isLead = large && blocks.length === 0
    blocks.push(<p key={`p-${blocks.length}`} className={isLead ? 'text-[17px] leading-[1.55]' : undefined}>{line}</p>)
  }
  flush()
  return (
    <div className={`${compact ? 'text-[13px]' : large ? 'text-[15px]' : 'text-sm'} leading-relaxed text-casevo-forest space-y-3`}>
      {blocks}
    </div>
  )
}

const DaySummaryCard: React.FC<DaySummaryCardProps> = ({ date, variant = 'dashboard', className = '', dataVersion }) => {
  const compact = variant === 'panel'
  const [summary, setSummary] = useState<DaySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Load (cached-or-generated) on mount / day change / retry / the day's data changing.
  // ★ NO LAYOUT SHIFT: only a NEW DAY (or a retry after an error) blanks the
  // card to its skeleton. A dataVersion change (an item added / moved) re-loads
  // SILENTLY behind the summary already on screen — it used to collapse the
  // card back to the skeleton with no user action, jumping everything below.
  const [loadedDate, setLoadedDate] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    if (loadedDate !== date || error) { setLoading(true); setSummary(null) }
    setError(null)
    setRefreshNote(null)
    fetchDaySummaryApi(date)
      .then((s) => { if (!cancelled) { setSummary(s); setLoadedDate(date) } })
      .catch((err) => { if (!cancelled) { setSummary(null); setError(errorMessage(err, "Couldn't load your summary.")) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // loadedDate / error are read only to decide skeleton-vs-silent; excluded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, reloadKey, dataVersion])

  const handleRefresh = useCallback(async (): Promise<void> => {
    setRefreshing(true)
    setRefreshNote(null)
    try {
      setSummary(await refreshDaySummaryApi(date))
    } catch (err) {
      setRefreshNote(errorMessage(err, "Couldn't refresh right now."))
    } finally {
      setRefreshing(false)
    }
  }, [date])

  const isAi = summary?.source === 'ai' || summary?.source === 'cache'
  const generatedAt = summary && isAi
    ? new Date(summary.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null

  // ── 'briefing' — the dashboard's two-part card (mockup option 2) ──
  // Values copied from the mockup: radius 16 · grid 236px | 1fr ·
  // left pad 24 + hairline divider + gradient(180deg, input → transparent) ·
  // spark tile 40×40 r12 · "Daily briefing" 18/600 · lead text 17px/1.55 ·
  // right pad 24/28 · foot 12px soft with a 32×32 r9 icon button.
  if (variant === 'briefing') {
    return (
      <section
        aria-label="AI day summary"
        aria-busy={loading || refreshing}
        className={`grid grid-cols-1 md:grid-cols-[236px_1fr] overflow-hidden rounded-[14px] border border-casevo-border bg-casevo-surface ${className}`}
      >
        {/* Left — spark + title */}
        <div className="p-6 border-b md:border-b-0 md:border-r border-casevo-border-soft
                        bg-[linear-gradient(180deg,#f2f8f5,transparent)] dark:bg-[linear-gradient(180deg,#2d3e34,transparent)]
                        flex md:block items-center gap-4">
          <div className="w-10 h-10 shrink-0 rounded-[12px] bg-casevo-surface border border-casevo-border flex items-center justify-center text-casevo-gold
                          shadow-[0_1px_2px_rgba(16,40,28,0.05),0_2px_6px_rgba(16,40,28,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <SparkleIcon size={20} />
          </div>
          <div>
            <div className="md:mt-4 text-[18px] font-semibold text-casevo-forest">Daily briefing</div>
            <div className="mt-1.5 text-[10.5px] tracking-[0.14em] uppercase font-semibold text-casevo-brand dark:text-[#74c69d]">AI · Your day</div>
          </div>
        </div>

        {/* Right — the summary + foot */}
        {/* Body min-height = the skeleton's (2 lead lines + a 2-line paragraph),
            so a short summary never shrinks the card under the content below. */}
        <div className="px-6 py-6 sm:px-7 flex flex-col justify-center min-w-0">
          <div className="min-h-[116px] flex flex-col justify-center">
          {loading ? (
            // Same shape as a real briefing: a 2-line 17px lead, a 15px paragraph.
            <div role="status" aria-label="Generating your briefing" className="space-y-3">
              <div>
                <span className="block h-[26px] leading-[26px]"><span className="inline-block align-middle h-3.5 w-11/12 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" /></span>
                <span className="block h-[26px] leading-[26px]"><span className="inline-block align-middle h-3.5 w-8/12 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" /></span>
              </div>
              <div>
                <span className="block h-[24px] leading-[24px]"><span className="inline-block align-middle h-3 w-10/12 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" /></span>
                <span className="block h-[24px] leading-[24px]"><span className="inline-block align-middle h-3 w-6/12 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" /></span>
              </div>
            </div>
          ) : error ? (
            <p className="text-[15px] text-casevo-text-soft">
              {error}{' '}
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="font-semibold text-casevo-brand dark:text-[#74c69d] underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid rounded-sm"
              >
                Try again
              </button>
            </p>
          ) : summary ? (
            summary.source === 'empty'
              ? <p className="text-[17px] leading-[1.55] text-casevo-forest">{summary.text}</p>
              : <SummaryBody text={summary.text} compact={false} large />
          ) : null}
          </div>

          {/* The foot row is ALWAYS there (disabled while loading), so it can't
              appear under the summary once it loads. */}
          {!error && (
            <div className="flex flex-wrap items-center gap-2.5 mt-3.5 text-[12px] text-casevo-text-soft">
              <button
                type="button"
                onClick={() => { void handleRefresh() }}
                disabled={refreshing || loading}
                className="inline-flex items-center gap-2.5 text-casevo-text-soft hover:text-casevo-forest disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus group/regen"
              >
                <span className="w-8 h-8 rounded-[9px] border border-casevo-border bg-casevo-surface text-casevo-mid inline-flex items-center justify-center
                                 group-hover/regen:border-casevo-mid group-focus-visible/regen:ring-2 group-focus-visible/regen:ring-casevo-mid transition-colors">
                  <RefreshIcon spinning={refreshing} />
                </span>
                <span>{refreshing ? 'Regenerating…' : 'Regenerate'}</span>
              </button>
              {generatedAt && !refreshing && <span>· Generated {generatedAt}</span>}
              <span className="sm:ml-auto">
                {summary?.source === 'fallback'
                  ? 'Showing a plain list — the AI summary will retry next time.'
                  : 'AI-generated from your calendar — check key details.'}
              </span>
            </div>
          )}
          {refreshNote && <p className="mt-2 text-[12px] text-casevo-text-soft" role="status">{refreshNote}</p>}
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label="AI day summary"
      aria-busy={loading || refreshing}
      className={`min-w-0 rounded-lg border border-casevo-border bg-casevo-surface ${compact ? 'p-4' : 'p-5'}`}
    >
      {/* Header: subtle AI marker + generated time + refresh */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-widest text-casevo-brand">
          <SparkleIcon className="text-casevo-gold" />
          <span>{compact ? 'AI · Day summary' : 'AI · Your day'}</span>
        </h2>
        <div className="flex items-center gap-2 text-xs text-casevo-text-soft">
          {generatedAt && !refreshing && <span>Generated {generatedAt}</span>}
          {refreshing && <span>Regenerating…</span>}
          <button
            type="button"
            onClick={() => { void handleRefresh() }}
            disabled={loading || refreshing}
            title="Regenerate summary"
            aria-label="Regenerate summary"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-casevo-text-soft
                       hover:text-casevo-forest hover:bg-casevo-mint transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid"
          >
            <RefreshIcon spinning={refreshing} />
          </button>
        </div>
      </div>

      {/* Body — min-height = the skeleton's, so a short summary doesn't
          shrink the panel under the buttons / entries below. */}
      <div className="min-h-[88px]">
      {loading ? (
        <div role="status" className="space-y-2.5">
          <p className="text-sm text-casevo-text-soft">Generating your summary…</p>
          <div className="h-3 w-11/12 rounded bg-casevo-border/60 animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-9/12 rounded bg-casevo-border/60 animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-10/12 rounded bg-casevo-border/60 animate-pulse motion-reduce:animate-none" />
        </div>
      ) : error ? (
        <div className="text-sm text-casevo-text-soft">
          {error}{' '}
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="underline text-casevo-brand hover:text-casevo-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid rounded-sm"
          >
            Try again
          </button>
        </div>
      ) : summary?.source === 'empty' ? (
        <p className="text-sm text-casevo-text-soft italic">{summary.text}</p>
      ) : summary ? (
        <>
          <SummaryBody text={summary.text} compact={compact} />
          {summary.source === 'fallback' && (
            <p className="mt-3 text-xs text-casevo-text-soft">Showing a plain list — the AI summary will retry next time.</p>
          )}
        </>
      ) : null}
      </div>

      {refreshNote && <p className="mt-3 text-xs text-casevo-text-soft" role="status">{refreshNote}</p>}

      {/* Quiet provenance note — AI output on confidential legal data. Also
          shown while loading (most summaries are AI), so it doesn't pop in
          under the summary when it arrives. */}
      {(isAi || loading) && (
        <p className="mt-3 pt-3 border-t border-casevo-border text-[11px] text-casevo-text-soft">
          AI-generated from your calendar — check key details before relying on them.
        </p>
      )}
    </section>
  )
}

export default DaySummaryCard
